import { useMemo, useState } from "react";
import { Panel } from "../common/Panel";
import { StaticCard } from "../card/StaticCard";
import { ToolPicker } from "../editor/ToolPicker";
import { TypePicker } from "../editor/TypePicker";
import { PalettePicker } from "../editor/PalettePicker";
import { FrameControls } from "../editor/FrameControls";
import { SceneLayerControls, SceneLayerPlacementControls } from "../editor/SceneLayerControls";
import { SpritePicker } from "../editor/SpritePicker";
import { normalizeCardDraft, type CardDraft, type CardType, type FrameStyleId, type FrameWeightId, type SceneLayerSlot, type ToolId } from "../../shared/game";
import { spriteToDraft } from "../../data/sprites";
import { getSuggestedSceneLayer } from "../../lib/card-scene";
import { findCardType } from "../../lib/card";

type LayoutVariant = "studio-strip" | "canvas-first" | "inspector";

const VARIANTS: Record<LayoutVariant, { title: string; summary: string; bestFor: string }> = {
  "studio-strip": {
    title: "Variant 1 · Studio Strip",
    summary: "A horizontal tool strip directly above the card. Mode switches stay close to the canvas, and the active controls open in a shallow band below the strip.",
    bestFor: "Fast switching and low travel distance between controls and card.",
  },
  "canvas-first": {
    title: "Variant 2 · Canvas First",
    summary: "The card dominates the page. Secondary controls collapse into a bottom dock so nothing competes with the artwork while drafting.",
    bestFor: "Most focused drawing experience and the clearest stage for the card.",
  },
  inspector: {
    title: "Variant 3 · Split Inspector",
    summary: "A compact left scene shelf paired with a slim right inspector. The card sits in a dedicated center lane, like a lightweight design tool.",
    bestFor: "Players who want structure and quick access without tall scrolling stacks.",
  },
};

function createShowcaseCard(): CardDraft {
  const draft = normalizeCardDraft(spriteToDraft("phanty"));
  return {
    ...draft,
    name: "Phanty",
    description: "Phases through walls at midnight and leaves behind lavender static.",
    sceneLayers: [
      getSuggestedSceneLayer("background", "moon-temple"),
      getSuggestedSceneLayer("character", "phanty"),
      getSuggestedSceneLayer("effect", "arcane-burst"),
    ],
  };
}

export function LayoutLab() {
  const [variant, setVariant] = useState<LayoutVariant>("studio-strip");
  const [tool, setTool] = useState<ToolId>("ink");
  const [brushSize, setBrushSize] = useState(2);
  const [card, setCard] = useState<CardDraft>(() => createShowcaseCard());
  const [activeSlot, setActiveSlot] = useState<SceneLayerSlot>("character");
  const [activeBand, setActiveBand] = useState<"sprites" | "paint" | "card" | "frame">("sprites");

  const activeType = findCardType(card.typeLabel);
  const roomCode = new URLSearchParams(window.location.search).get("room") ?? "JD83FY";
  const variantMeta = VARIANTS[variant];

  const spritePanel = (
    <div className="control-stack">
      <SceneLayerControls
        card={card}
        activeSlot={activeSlot}
        onSelectSlot={setActiveSlot}
        onMoveLayer={() => {}}
        onScaleLayer={() => {}}
        onClearLayer={() => {}}
      />
      <SpritePicker
        activeSlot={activeSlot}
        selectedSpriteId={card.sceneLayers.find((layer) => layer.slot === activeSlot)?.spriteId ?? null}
        onLoadSprite={(spriteId) => {
          setCard((previous) => ({
            ...previous,
            sceneLayers: previous.sceneLayers.map((layer) => (
              layer.slot === activeSlot ? getSuggestedSceneLayer(activeSlot, spriteId) : layer
            )),
          }));
        }}
        onClear={() => {
          setCard((previous) => ({
            ...previous,
            sceneLayers: previous.sceneLayers.map((layer) => (
              layer.slot === activeSlot ? getSuggestedSceneLayer(activeSlot, null) : layer
            )),
          }));
        }}
      />
      <SceneLayerPlacementControls
        card={card}
        activeSlot={activeSlot}
        onMoveLayer={(slot, dx, dy) => {
          setCard((previous) => ({
            ...previous,
            sceneLayers: previous.sceneLayers.map((layer) => (
              layer.slot === slot ? { ...layer, offsetX: layer.offsetX + dx, offsetY: layer.offsetY + dy } : layer
            )),
          }));
        }}
        onScaleLayer={() => {}}
        onClearLayer={() => {}}
      />
    </div>
  );

  const paintPanel = (
    <div className="control-stack">
      <ToolPicker activeTool={tool} onSelect={setTool} brushSize={brushSize} onBrushSizeChange={setBrushSize} />
      <PalettePicker color="#c77dff" onSelect={() => {}} />
    </div>
  );

  const cardPanel = (
    <div className="control-stack">
      <div className="builder-card">
        <div className="builder-card-header">
          <span className="control-label">Element</span>
          <span className="control-helper">Attack identity</span>
        </div>
        <TypePicker
          typeLabel={card.typeLabel}
          onSelect={(typeLabel) => setCard((previous) => ({ ...previous, typeLabel: typeLabel as CardType["label"] }))}
        />
      </div>
      <div className="builder-card">
        <div className="builder-card-header">
          <span className="control-label">Health</span>
          <span className="control-helper" style={{ color: activeType.color, fontWeight: 700 }}>HP {card.hp}</span>
        </div>
        <input type="range" min="10" max="200" step="10" value={card.hp} onChange={(event) => setCard((previous) => ({ ...previous, hp: Number(event.target.value) }))} />
      </div>
    </div>
  );

  const framePanel = (
    <FrameControls
      frameStyle={card.frameStyle}
      setFrameStyle={(frameStyle) => setCard((previous) => ({ ...previous, frameStyle: frameStyle as FrameStyleId }))}
      frameWeight={card.frameWeight}
      setFrameWeight={(frameWeight) => setCard((previous) => ({ ...previous, frameWeight: frameWeight as FrameWeightId }))}
      autoColor={activeType.color}
      frameColorManual={card.frameColorManual}
      setFrameColorManual={(frameColorManual) => setCard((previous) => ({ ...previous, frameColorManual }))}
    />
  );

  const activeContext = useMemo(() => {
    if (activeBand === "sprites") {
      return spritePanel;
    }
    if (activeBand === "paint") {
      return paintPanel;
    }
    if (activeBand === "card") {
      return cardPanel;
    }
    return framePanel;
  }, [activeBand, cardPanel, framePanel, paintPanel, spritePanel]);

  const cardStage = (
    <Panel title="Card Stage" accent={activeType.color} className="editor-panel">
      <div className="layout-lab-stage-meta">
        <span>{variantMeta.title}</span>
        <span>{variantMeta.bestFor}</span>
      </div>
      <div className="layout-lab-card-wrap">
        <StaticCard card={card} scale={0.82} />
      </div>
    </Panel>
  );

  const variantBody = (() => {
    if (variant === "studio-strip") {
      return (
        <div className="layout-lab-rethink-shell">
          <div className="layout-lab-strip-toolbar">
            {(["sprites", "paint", "card", "frame"] as const).map((entry) => (
              <button key={entry} type="button" className={activeBand === entry ? "active-button" : "ghost-button"} onClick={() => setActiveBand(entry)}>
                {entry === "sprites" ? "Sprites" : entry === "paint" ? "Paint" : entry === "card" ? "Card" : "Frame"}
              </button>
            ))}
          </div>
          <Panel title="Active Controls" accent={activeBand === "sprites" ? "#7aa7ff" : activeType.color}>
            <div className="layout-lab-active-band">{activeContext}</div>
          </Panel>
          {cardStage}
        </div>
      );
    }

    if (variant === "canvas-first") {
      return (
        <div className="layout-lab-rethink-shell">
          {cardStage}
          <div className="layout-lab-canvas-dock">
            <Panel title="Assets" accent="#7aa7ff">
              {spritePanel}
            </Panel>
            <Panel title="Paint" accent={activeType.color}>
              {paintPanel}
            </Panel>
            <Panel title="Card Setup" accent={activeType.color}>
              {cardPanel}
            </Panel>
            <Panel title="Frame" accent={activeType.color}>
              {framePanel}
            </Panel>
          </div>
        </div>
      );
    }

    return (
      <div className="layout-grid layout-lab-inspector">
        <aside className="sidebar-column">
          <Panel title="Scene Shelf" accent="#7aa7ff">
            {spritePanel}
          </Panel>
        </aside>
        <section className="workspace-column">
          {cardStage}
        </section>
        <aside className="preview-column">
          <Panel title="Inspector" accent={activeType.color}>
            <div className="control-stack">
              {paintPanel}
              {cardPanel}
              {framePanel}
            </div>
          </Panel>
        </aside>
      </div>
    );
  })();

  return (
    <main className="page-shell">
      <h1 className="page-title" style={{ color: activeType.color, textShadow: `0 0 20px ${activeType.color}` }}>
        BGJ Designer Rethink Lab
      </h1>

      <div className="room-topbar">
        <div className="room-topbar-meta">
          <div className="room-topbar-chip"><span>Status</span><strong>concept lab</strong></div>
          <div className="room-topbar-chip"><span>Room</span><strong>{roomCode}</strong></div>
          <div className="room-topbar-chip"><span>Goal</span><strong>Choose a cleaner editor architecture</strong></div>
        </div>
        <div className="room-topbar-actions">
          {(["studio-strip", "canvas-first", "inspector"] as LayoutVariant[]).map((entry) => (
            <button key={entry} type="button" className={variant === entry ? "active-button" : "ghost-button"} onClick={() => setVariant(entry)}>
              {VARIANTS[entry].title}
            </button>
          ))}
          <a className="ghost-button" href="/">
            Back To Game
          </a>
        </div>
        <div className="room-topbar-subline">
          <span>{variantMeta.summary}</span>
        </div>
      </div>

      {variantBody}
    </main>
  );
}
